import {
  createContext,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useContext,
  useId,
  useState,
} from "react";
import { FormikValues } from "formik";
import { toast } from "sonner";
import {
  GenericForm,
  GenericFormProps,
} from "@/lib/Components/Form/Forms/GenericForm";
import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import { Button } from "@/components/catalyst/button";
import { ArrowUpTrayIcon, PencilSquareIcon } from "@heroicons/react/16/solid";
import { cn } from "@/lib/utils";
import { DropzoneState } from "react-dropzone";

type EditableRowsContextValue = {
  openId: string | null;
  setOpenId: (id: string | null) => void;
};

const EditableRowsContext = createContext<EditableRowsContextValue | null>(
  null,
);

export function EditableRowsProvider({ children }: { children: ReactNode }) {
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <EditableRowsContext value={{ openId, setOpenId }}>
      {children}
    </EditableRowsContext>
  );
}

function useEditableRows(): EditableRowsContextValue {
  const context = useContext(EditableRowsContext);

  if (!context) {
    throw new Error(
      "Editable rows must be rendered within an EditableRowsProvider",
    );
  }

  return context;
}

export function SettingRowsCard({
  title,
  description,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-md bg-white shadow-sm">
      <div className="px-4 py-4 sm:px-6">
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          {title}
        </h3>
        {description ? (
          <p className="mt-1 text-sm text-gray-500">{description}</p>
        ) : null}
      </div>
      <div className="divide-y divide-gray-100 border-t border-gray-100">
        {children}
      </div>
    </div>
  );
}

function rowActivationProps(onOpen: () => void) {
  return {
    role: "button" as const,
    tabIndex: 0,
    onClick: onOpen,
    onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onOpen();
      }
    },
  };
}

function RowAction({
  label,
  icon: Icon = PencilSquareIcon,
}: {
  label: string;
  icon?: typeof PencilSquareIcon;
}) {
  return (
    <span className="flex shrink-0 items-center gap-1 text-sm font-semibold text-indigo-600 opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100">
      <Icon className="size-4" />
      {label}
    </span>
  );
}

function EmptyValue({ label }: { label: string }) {
  return <span className="italic text-gray-400">{label}</span>;
}

function hasContent(value: ReactNode) {
  return value !== null && value !== undefined && value !== "";
}

function CollapsedRow({
  label,
  value,
  emptyLabel,
  actionLabel,
  actionIcon,
  onOpen,
}: {
  label: ReactNode;
  value: ReactNode;
  emptyLabel: string;
  actionLabel: string;
  actionIcon?: typeof PencilSquareIcon;
  onOpen: () => void;
}) {
  return (
    <div
      {...rowActivationProps(onOpen)}
      className="group flex cursor-pointer items-center gap-4 px-4 py-3 hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50 sm:px-6"
    >
      <div className="w-40 shrink-0 text-sm font-medium text-gray-500">
        {label}
      </div>
      <div className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">
        {hasContent(value) ? value : <EmptyValue label={emptyLabel} />}
      </div>
      <RowAction label={actionLabel} icon={actionIcon} />
    </div>
  );
}

function StackedRow({
  label,
  value,
  emptyLabel,
  actionLabel,
  onOpen,
}: {
  label: ReactNode;
  value: ReactNode;
  emptyLabel: string;
  actionLabel: string;
  onOpen: () => void;
}) {
  return (
    <div
      {...rowActivationProps(onOpen)}
      className="group flex cursor-pointer flex-col gap-2 px-4 py-3 hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50 sm:px-6"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="text-sm font-medium text-gray-500">{label}</div>
        <RowAction label={actionLabel} />
      </div>
      <div className="whitespace-pre-wrap break-words text-sm font-medium text-gray-900">
        {hasContent(value) ? value : <EmptyValue label={emptyLabel} />}
      </div>
    </div>
  );
}

export function StaticSettingRow({
  label,
  value,
  emptyLabel = "Not set",
}: {
  label: ReactNode;
  value: ReactNode;
  emptyLabel?: string;
}) {
  return (
    <div className="flex items-center gap-4 px-4 py-3 sm:px-6">
      <div className="w-40 shrink-0 text-sm font-medium text-gray-500">
        {label}
      </div>
      <div className="min-w-0 flex-1 text-sm font-medium text-gray-900">
        {hasContent(value) ? value : <EmptyValue label={emptyLabel} />}
      </div>
    </div>
  );
}

export function DropzoneSettingRow({
  label,
  dropzone,
  actionLabel,
  children,
}: {
  label: ReactNode;
  dropzone: DropzoneState;
  actionLabel: string;
  children: ReactNode;
}) {
  return (
    <div
      {...dropzone.getRootProps()}
      className={cn(
        "group flex cursor-pointer items-center gap-4 px-4 py-3 hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50 sm:px-6",
        dropzone.isDragActive && "bg-indigo-50 hover:bg-indigo-50",
      )}
    >
      <div className="w-40 shrink-0 text-sm font-medium text-gray-500">
        <input {...dropzone.getInputProps()} />
        {label}
      </div>
      <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5">
        {children}
      </div>
      <RowAction label={actionLabel} icon={ArrowUpTrayIcon} />
    </div>
  );
}

type EditableRowProps<T extends FormikValues> = {
  label: ReactNode;
  value: ReactNode;
  emptyLabel?: string;
  editLabel?: string;
  savedMessage?: string;
  editClassName?: string;
  children: ReactNode;
  onSave: (values: T) => Promise<unknown>;
} & Omit<GenericFormProps<T>, "children" | "onSubmit">;

export function EditableSettingRow<T extends FormikValues>(
  props: EditableRowProps<T>,
) {
  return <EditableRow {...props} collapsedRow={CollapsedRow} />;
}

/** Same as `EditableSettingRow`, but shows the value in full instead of truncating it to one line. */
export function EditableMultilineSettingRow<T extends FormikValues>(
  props: EditableRowProps<T>,
) {
  return <EditableRow {...props} collapsedRow={StackedRow} />;
}

function EditableRow<T extends FormikValues>({
  label,
  value,
  emptyLabel = "Not set",
  editLabel = "Edit",
  savedMessage = "Settings saved successfully",
  editClassName = "max-w-md",
  children,
  onSave,
  collapsedRow: CollapsedRowComponent,
  ...formProps
}: EditableRowProps<T> & {
  collapsedRow: typeof CollapsedRow | typeof StackedRow;
}) {
  const rowId = useId();
  const { openId, setOpenId } = useEditableRows();
  const isEditing = openId === rowId;
  const focusFirstField = useFocusFirstField();

  if (!isEditing) {
    return (
      <CollapsedRowComponent
        label={label}
        value={value}
        emptyLabel={emptyLabel}
        actionLabel={editLabel}
        onOpen={() => setOpenId(rowId)}
      />
    );
  }

  return (
    <div className="bg-gray-50 px-4 py-4 sm:px-6">
      <GenericForm<T>
        initialEditingState
        onSubmit={async (values) => {
          await onSave(values);
          toast.success(savedMessage);
          setOpenId(null);
        }}
        {...formProps}
      >
        <div className="col-span-full space-y-4">
          <div ref={focusFirstField} className={cn("space-y-4", editClassName)}>
            {children}
          </div>
          <RowActions onCancel={() => setOpenId(null)} />
        </div>
      </GenericForm>
    </div>
  );
}

/**
 * Stable ref callback that focuses the first editable control when the editor
 * mounts. Stable identity means it only runs on mount/unmount, so typing (which
 * re-renders the form) never yanks focus back.
 */
function useFocusFirstField() {
  return useCallback((node: HTMLDivElement | null) => {
    node
      ?.querySelector<HTMLElement>(
        'input, textarea, select, [contenteditable="true"], button[role="combobox"]',
      )
      ?.focus();
  }, []);
}

function RowActions({ onCancel }: { onCancel: () => void }) {
  const { isSubmitting } = useFormState();

  return (
    <div className="flex gap-2">
      <Button
        type="submit"
        color="indigo"
        isLoading={isSubmitting}
        showCommandShortcut
      >
        Save
      </Button>
      <Button plain type="button" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}
